import { useEffect, useRef, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSmoothScroll } from "@/hooks/use-smooth-scroll";
import { localizedPath } from "@/lib/site";
import { heroCopy, heroVideo } from "@/data/hero";
import HeroAtmosphere from "./hero/HeroAtmosphere";
import HeroVideo from "./hero/HeroVideo";
import { useMotionPreference } from "./hero/useMotionPreference";
import "./hero/hero-navigation.css";

export default function Hero() {
  const { language } = useLanguage();
  const copy = heroCopy[language], scrollTo = useSmoothScroll();
  const section = useRef<HTMLElement>(null), content = useRef<HTMLDivElement>(null);
  const reduced = useMotionPreference();
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    let intersecting = true;
    const update = () => setVisible(intersecting && !document.hidden);
    const observer = new IntersectionObserver(([entry]) => { intersecting = entry.isIntersecting; update(); });
    if (section.current) observer.observe(section.current);
    document.addEventListener("visibilitychange", update); update();
    return () => { observer.disconnect(); document.removeEventListener("visibilitychange", update); };
  }, []);
  useEffect(() => {
    if (reduced || matchMedia("(prefers-reduced-motion: reduce)").matches || !content.current) return;
    const animations = Array.from(content.current.querySelectorAll<HTMLElement>("[data-entrance]")).map((node, index) => node.animate([
      { opacity: .55, transform: `translateY(${index < 2 ? 12 : 8}px)` }, { opacity: 1, transform: "none" },
    ], { duration: 650, delay: [40, 130, 220, 270, 340][index], easing: "cubic-bezier(.22,1,.36,1)", fill: "backwards" }));
    const stop = () => animations.forEach(animation => animation.cancel());
    const block = content.current; block.addEventListener("focusin", stop);
    return () => { stop(); block.removeEventListener("focusin", stop); };
  }, [reduced, language]);
  return <section ref={section} id="home" className="ibt-design ibt-hero" dir={language === "ar" ? "rtl" : "ltr"}
    data-motion-paused={reduced || !visible ? "true" : undefined}>
    <HeroAtmosphere />
    <div className="ibt-container">
      <div className="ibt-copy" ref={content}>
        <h1><span data-entrance>{copy.main}</span>{" "}<span className="ibt-tagline-end" data-entrance>{copy.end}</span></h1>
        <p className="ibt-service" data-entrance>{copy.service}</p>
        <p className="ibt-description" data-entrance>{copy.description}</p>
        <div className="ibt-cta-row" data-entrance>
          <a className="ibt-cta ibt-primary" href={localizedPath(language, "/#contact")} onClick={event => { event.preventDefault(); scrollTo("#contact"); }}>{copy.start}</a>
          <a className="ibt-cta ibt-secondary" href={localizedPath(language, "/#portfolio")} onClick={event => { event.preventDefault(); scrollTo("#portfolio"); }}>{copy.work}</a>
        </div>
      </div>
      {/* The entire video section stays hidden until the original asset is approved. */}
      {heroVideo.enabled && heroVideo.src && <>
        {/* Keep the caption space empty when the video section is enabled. */}
        <div className="ibt-video-caption" aria-hidden="true" />
        <HeroVideo />
      </>}
    </div>
  </section>;
}
